import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { TsBaseDialog, TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TsButtonModule, TSIconModule, TsInputComponent } from 'tsui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { TeamsService, WithPathAndDepth } from '../services/teams.service';
import { map } from 'rxjs/operators';
import { BehaviorSubject, iif, Observable, of, shareReplay, tap } from 'rxjs';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { Team } from '@core/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';

export interface TeamsManagementForm {
  name: string;
  parent_id: number;
}

export interface TeamOption {
  label: string;
  value: string;
  disabled: boolean;
}

export type TeamsManagementDialogResult = TeamsManagementForm | null;

export interface TeamsManagementDialogData {
  editableTeam: Team;
}

@Component({
  selector: 'teams-management-dialog',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    ReactiveFormsModule,
    TsInputComponent,
    TsSelectModule,
    AsyncPipe,
    TsSkeletonComponent,
    TSIconModule,
  ],
  templateUrl: './teams-management-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsManagementDialogComponent implements OnInit, TsBaseDialog<TeamsManagementDialogResult> {
  private readonly teamsService = inject(TeamsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly docRef = inject(DOCUMENT);

  readonly disableTooltipMessage = new BehaviorSubject(undefined);
  readonly editableTeam = inject<TeamsManagementDialogData>(DIALOG_DATA)?.editableTeam;
  readonly teamsForm = this.fb.group({
    name: ['', Validators.required],
    parent_id: ['', Validators.required],
  });
  teamsOptions$: Observable<TeamOption[]>;
  editMode = !!this.editableTeam;

  get saveButtonLabel(): string {
    return !this.editableTeam ? 'Create' : 'Edit';
  }

  private get teamHasChildren(): boolean {
    return this.editableTeam?.children?.length > 0;
  }

  ngOnInit() {
    this.teamsOptions$ = this.getTeamOptions();
  }

  closeDialog(result?: TeamsManagementDialogResult): void {
    if (this.teamsForm.invalid || !result) {
      this.dialogRef.close();
    } else {
      this.dialogRef.close(result);
    }
  }

  private getTeamOptions(): Observable<TeamOption[]> {
    const isEditableRootTeam = !!this.editableTeam && !this.editableTeam?.parent_id;

    const teamOptionsFlow$ = this.teamsService.getTeams(1, 1000).pipe(
      tap(({ results }) => this.initForm(results)),
      map(({ results }) => TeamsService.flattenTeamsAndAddMetadata(results)),
      map((teams) => this.filterEditableTeam(teams)),
      map(TeamsManagementDialogComponent.convertTeamsToOption),
    );

    const rootTeamFlow$ = of([]).pipe(tap(() => this.initEditForm()));

    return iif(() => isEditableRootTeam, rootTeamFlow$, teamOptionsFlow$).pipe(
      tap(() => {
        (this.docRef.activeElement as any).blur();
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay(),
    );
  }

  private initForm(teams: Team[]): void {
    if (this.editableTeam) {
      this.initEditForm();
    } else {
      this.initCreateForm(teams);
    }
  }

  private initCreateForm(teams: Team[]) {
    // when a team has team.parent_id === null, it's the root parent Team
    const rootTeam = teams?.find((team) => !team.parent_id);

    if (!rootTeam) return;

    this.teamsForm.get('parent_id').setValue(rootTeam.id.toString(), { emitEvent: false });
  }

  private initEditForm() {
    const editableTeamFormData = {
      name: this.editableTeam.name,
      parent_id: this.editableTeam?.parent_id?.toString(),
    };

    if (!editableTeamFormData.parent_id || this.teamHasChildren) {
      this.teamsForm.get('parent_id').disable();

      const message = !editableTeamFormData.parent_id
        ? 'This team is the root team and cannot have a parent team'
        : 'Teams with children cannot reassign their parent team';

      this.disableTooltipMessage.next(message);
    }

    this.teamsForm.patchValue(editableTeamFormData);
  }

  private filterEditableTeam(teams: Team[]): Team[] {
    if (this.editableTeam) {
      return teams?.filter(({ id }) => id !== this.editableTeam?.id);
    }

    return teams;
  }

  private static convertTeamsToOption(teams: (Team & WithPathAndDepth)[]): TeamOption[] {
    const maxTeamDepth = 3;

    return (
      teams?.reduce(
        (acc, team) => {
          return [
            ...acc,
            {
              label: team.name,
              value: team.id.toString(),
              disabled: team.depth >= maxTeamDepth,
            },
          ];
        },
        <TeamOption[]>[],
      ) ?? []
    );
  }
}
